import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { Alert, Box, Button, FormControl, FormControlLabel, Grid, Paper, Slider, Switch, Typography, } from '@mui/material';
import { get_z3k_object_state } from 'z3k-config';
import { getHelpText } from './help-content';
import { Z3KSettings } from 'console/device-settings/z3k/provider/types';
import { selectDevice } from 'entities/device/lib';
import { HelpIcon } from 'console/shared/components/fields/help-icon';
export const Tester = memo(function Tester(props) {
    const { objectId, help_id, help_options } = props;
    const { device_id, sendCommand } = useContext(Z3KSettings);
    const selectedDevice = usePartialAppSelector(selectDevice, device_id);
    const sendTestCommand = useCallback((command_args) => {
        const commandName = typeof command_args.isOn === 'number'
            ? 'SetTestTap'
            : 'SetTestPumpOrRelay';
        sendCommand(objectId, commandName, command_args);
    }, [sendCommand, objectId]);
    if (!selectedDevice) {
        return null;
    }
    const objectState = get_z3k_object_state(selectedDevice.z3k_state, objectId);
    if (!objectState) {
        return (_jsx(Grid, { item: true, xs: 12, children: _jsx(Alert, { severity: "warning", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u0435 \u043A\u043E\u043D\u0444\u0438\u0433\u0443\u0440\u0430\u0446\u0438\u044E \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430, \u0447\u0442\u043E\u0431\u044B \u043F\u043E\u044F\u0432\u0438\u043B\u0441\u044F \u0440\u0435\u0436\u0438\u043C \u0442\u0435\u0441\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F." }) }));
    }
    return (_jsx(TesterContent, { objectId: objectId, objectState: objectState, sendCommand: sendTestCommand, help_id: help_id, help_options: help_options }));
});
const TesterContent = (props) => {
    const { objectState, sendCommand, objectId, help_id, help_options } = props;
    const [loading, setLoading] = useState(false);
    const show_help_icon = useMemo(() => help_id != null && getHelpText(help_id, help_options ?? null) !== null, [help_id, help_options]);
    useEffect(() => {
        setLoading(false);
    }, [objectState]);
    const onSwitchChange = () => {
        setLoading(true);
        if ('active' in objectState) {
            sendCommand({
                isOn: objectState.active,
                isTest: !objectState.test_mode,
            });
        }
        else if ('direction' in objectState) {
            sendCommand({
                isOn: 1,
                isTest: !objectState.test_mode,
            });
        }
        else {
            sendCommand({
                isOn: objectState.run,
                isTest: !objectState.test_mode,
            });
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", children: [_jsx(FormControl, { component: "fieldset", children: _jsx(FormControlLabel, { value: "start", control: _jsx(Switch, { color: "primary", checked: objectState.test_mode, disabled: loading, onChange: onSwitchChange }), style: { marginRight: 10 }, label: 'Режим тестирования: ' +
                                (loading
                                    ? 'Отправка...'
                                    : objectState.test_mode
                                        ? 'Включён'
                                        : 'Выключен'), labelPlacement: "end" }) }), show_help_icon && (_jsx(HelpIcon, { help_id: help_id, help_options: help_options }))] }), objectState.test_mode && (_jsx(Paper, { elevation: 3, sx: {
                    p: 3,
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }, children: _jsx("div", { className: 'field', children: 'run' in objectState ? (_jsx(PumpTesterContent, { objectId: objectId, objectState: objectState, sendCommand: (command_args) => sendCommand(command_args) })) : 'direction' in objectState ? (_jsx(TapTesterContent, { objectId: objectId, objectState: objectState, sendCommand: (command_args) => sendCommand(command_args) })) : (_jsx(RelayTesterContent, { objectId: objectId, objectState: objectState, sendCommand: (command_args) => sendCommand(command_args) })) }) }))] }));
};
const PumpTesterContent = (props) => {
    const [disable, setDisable] = useState(false);
    useEffect(() => {
        setDisable(false);
    }, [props.objectState]);
    return (_jsxs(Button, { variant: 'outlined', title: props.objectState?.run ? 'Выключить' : 'Включить', disabled: disable, onClick: () => {
            setDisable(true);
            props.sendCommand({
                isOn: !props.objectState?.run,
                isTest: true,
            });
        }, children: [props.objectState?.run ? 'Выключить' : 'Включить', " \u043D\u0430\u0441\u043E\u0441"] }));
};
const RelayTesterContent = (props) => {
    const [disable, setDisable] = useState(false);
    useEffect(() => {
        setDisable(false);
    }, [props.objectState]);
    return (_jsxs(Button, { variant: 'outlined', title: props.objectState?.active ? 'Выключить' : 'Включить', disabled: disable, onClick: () => {
            setDisable(true);
            props.sendCommand({
                isOn: !props.objectState?.active,
                isTest: true,
            });
        }, children: [props.objectState?.active ? 'Выключить' : 'Включить', " \u0440\u0435\u043B\u0435"] }));
};
const TapTesterContent = (props) => {
    const [percent, setPercent] = useState(0);
    const [disable, setDisable] = useState(false);
    useEffect(() => {
        setDisable(false);
    }, [props.objectState]);
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { gutterBottom: true, children: "\u041F\u0440\u043E\u0446\u0435\u043D\u0442 \u043E\u0442\u043A\u0440\u044B\u0442\u0438\u044F" }), _jsx("div", { style: { padding: ' 0  15px 0 15px' }, children: _jsx(Slider, { value: percent, "aria-labelledby": "discrete-slider-restrict", step: 1, marks: [
                        {
                            value: 0,
                            label: '0%',
                        },
                        {
                            value: 100,
                            label: '100%',
                        },
                    ], valueLabelDisplay: "auto", onChange: (event, value) => {
                        setPercent(value);
                    } }) }), props.objectState.test_calibration && (_jsx(Alert, { style: { marginBottom: '5px' }, severity: "warning", children: "\u0412\u044B\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F \u043A\u0430\u043B\u0438\u0431\u0440\u043E\u0432\u043A\u0430" })), _jsx(Button, { variant: 'outlined', title: 'Установить', disabled: props.objectState.test_calibration || disable, onClick: () => {
                    setDisable(true);
                    props.sendCommand({
                        isOn: percent,
                        isTest: true,
                    });
                }, children: "\u0423\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C" })] }));
};
